import { useLayoutEffect } from 'react';

/**
 * [MCV-1563] Reserves the root scrollbar's width for as long as the public /
 * signed-out shell is mounted, and only then.
 *
 * The public site scrolls the document, and only its landing page fits in the
 * viewport, so navigating off "/" used to remove the scrollbar and slide the
 * centered header 15px sideways on any platform with classic scrollbars.
 *
 * It cannot be done globally on `html`. Authenticated shells deliberately fill
 * exactly the viewport and scroll through `Layout`'s inner `overflow-y-auto`
 * main column, so that the header, sidebar and footer stay pinned (see the
 * comment on that column). Forcing the root to scroll there paints a second,
 * permanently inert track down the far right of every Vault and HLX page --
 * measured: the real inner scroller ends at x=1425, the dead track sits at
 * x=1438, and a wheel over it moves nothing while the same gesture on the base
 * build scrolls the main column. That is the conventional far-right scroll
 * target, so it is not a cosmetic wart.
 *
 * A mounted-lifetime class is the narrowest fix available: `scrollbar-gutter`
 * does not work on the root element at all (its overflow propagates to the
 * viewport and html's own used value stays `visible`), so the reservation has
 * to be `overflow-y: scroll`, which is inherently a root-level switch.
 */
export const PublicScrollbarGutter = () => {
  // useLayoutEffect, not useEffect. A passive effect runs AFTER paint, so a cold
  // PublicApp load or a signed-in -> signed-out shell swap would paint the
  // header once at full width and then shift it when the class landed -- the
  // exact jump this component exists to prevent, just relocated to the shell
  // boundary. The cleanup is synchronous for the same reason in reverse:
  // otherwise the authenticated shell gets one frame with the dead outer track.
  //
  // Safe here because the app is client-rendered (`createRoot` in main.tsx,
  // no hydration), so there is no SSR pass to warn about.
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.add('public-shell-scroll');
    return () => root.classList.remove('public-shell-scroll');
  }, []);

  return null;
};
